import { RESPONSE_TYPE } from 'utils/constants/mimeTypes'
import { get, post, put, remove } from './api'

/**
 * get the automatic questions data, returns a promise
 * @param {string} token JWT Bearer Token
 * @param {string} applicationUUID application id to search related questions
 * @returns Promise of the request to be manipulated by the user
 */
export const getExtraAutomaticQuestions = (token, applicationUUID) => {
  return get({
    url: '/extra_auto_question/list',
    token,
    params: { application_uuid: applicationUUID },
  })
}

/**
 * gets the ongoing application of logged in candidate
 * @param {string} token JWT Bearer Token
 * @returns Promise of the request to be manipulated by the user
 */
export const getOngoingApplication = (token) => {
  return get({
    url: '/applications/ongoing',
    token,
  })
}

/**
 * Get application data by id
 * @param {string} token JWT Bearer Token
 * @param {string} applicationUUID string - application uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const getApplicationById = (token, applicationUUID) => {
  return get({
    url: `/applications/${applicationUUID}`,
    token: token,
  })
}

/**
 * get reviews list associated with an application
 * @param {string} token JWT Bearer Token
 * @param {string} applicationUUID application id to search related questions
 * @returns Promise of the request to be manipulated by the user
 */
export const getApplicationReviews = (token, applicationUUID) => {
  return get({
    url: `/reviews/`,
    token: token,
    params: { application_uuid: applicationUUID },
  })
}

/**
 * get all reviews list, can be parametrized
 * @param {string} token JWT Bearer Token
 * @param {string} options object that receives params object to the request
 * @returns Promise of the request to be manipulated by the user
 */
export const getAllReviews = (token, { params }) => {
  return get({
    url: `/reviews`,
    token: token,
    params: params,
  })
}

/**
 * get form data by id
 * @param {string} token JWT Bearer Token
 * @param {string} formID searched form id
 * @returns Promise of the request to be manipulated by the user
 */
export const getDueDiligenceForm = (token, formID) => {
  return get({
    url: `/due_diligence/pf/form/${formID}/find`,
    token: token,
  })
}

/**
 * Get attachment link
 * @param {string} token JWT Bearer Token
 * @param {string} attachmentID UUID of the attachment
 * @returns Promise of the request to be manipulated by the user
 */
export const getAttachmentDownloadLink = (token, attachmentID) => {
  return get({
    url: `/attachment/${attachmentID}/find`,
    token: token,
  })
}


/**
 * Get attachment link
 * @param {string} token JWT Bearer Token
 * @param {string} attachmentID UUID of the attachment
 * @returns Promise of the request to be manipulated by the user
 */
export const getContractOfAdhesionDownloadLink = (token, fileType) => {
  return get({
    url: `/attachment/terms/${fileType}`,
    token: token,
  })
}


/**
 * get images, person info and match info
 * @param {string} token JWT Bearer Token
 * @param {string} antiFraudID searched anti fraud id
 * @returns Promise of the request to be manipulated by the user
 */
export const getAntiFraudDetails = (token, antiFraudID) => {
  return get({
    url: '/antifraude/request',
    token: token,
    params: { _id: antiFraudID },
  })
}

/**
 * Submits the extra auto question answer to api
 * @param {string} token JWT Bearer Token
 * @param {string} questionID ID of answered question
 * @param {object} data form response of the question
 * @returns Promise of the request to be manipulated by the user
 */
export const submitExtraAutoAnswer = (token, questionID, data) => {
  return post({
    url: `/extra_auto_question/${questionID}/submit_answer`,
    token,
    data: data,
  })
}

/**
 * Submits the new password to the api
 * @param {string} token JWT Bearer Token
 * @param {string} operationType inform if it will create or update a password
 * @param {object} data new password form data
 * @returns Promise of the request to be manipulated by the user
 */
export const submitNewPassword = (token, operationType, data) => {
  return post({
    url: `/user/password-${operationType === 'creation' ? 'create' : 'change'}`,
    data,
    token,
  })
}

/**
 * get a list with all open questions meade to the user during manual analysis
 * @param {string} token JWT Bearer Token
 * @param {string} applicationUUID ID of application to get the questions
 * @returns Promise of the request to be manipulated by the user
 */
export const getApplicationOpenQuestions = (token, applicationUUID) => {
  return get({
    url: '/extra_open_question/list',
    token: token,
    params: {
      application_uuid: applicationUUID,
    },
  })
}

/**
 * create a new open question to a given application
 * @param {string} token JWT Bearer Token
 * @param {string} applicationUUID application ID to associate the question on creation
 * @param {object} data object with fields text and subject of type string
 * @returns Promise of the request to be manipulated by the user
 */
export const createOpenQuestion = (token, applicationUUID, data) => {
  return post({
    url: '/extra_open_question/question',
    token: token,
    params: {
      application_uuid: applicationUUID,
    },
    data: data,
  })
}

/**
 * Submit a new address OCR Rquest
 * @param {string} token JWT Bearer Token
 * @param {object} data object contained the ORC input params
 * @returns Promise of the request to be manipulated by the user
 */
export const postRequestAddressOcr = (token, data) => {
  return post({
    url: '/address-ocr/scan',
    token: token,
    data: data,
  })
}

/**
 * Request to skip the address OCR validation.
 * @param {string} token JWT Bearer Token
 * @param {string} formID ID of the form
 * @param {string} attachmentID ID of the last submitted
 * @returns Promise of the request to be manipulated by the user
 */
export const postSkipAddressOcr = (token, formID, attachmentID) => {
  return post({
    url: `/address-ocr/continue-without-validation`,
    params: { form_uuid: formID, attachment_uuid: attachmentID },
    token: token,
  })
}

/**
 * Get the address OCR data created on a form. It will contain the attempts information.
 * @param {string} token JWT Bearer Token
 * @param {string} formID ID of the form
 * @returns Promise of the request to be manipulated by the user
 */
export const getAddressOcrData = (token, formID) => {
  return get({
    url: `/address-ocr`,
    params: { form_uuid: formID },
    token: token,
  })
}

/**
 * submits the answer to an open question
 * @param {string} token JWT Bearer Token
 * @param {string} questionID ID of quetion to be answered
 * @param {object} data object with response
 * @returns Promise of the request to be manipulated by the user
 */
export const submitOpenQuestion = (token, questionID, data) => {
  return post({
    url: `/extra_open_question/${questionID}/submit_answer`,
    token: token,
    data: data,
  })
}

/**
 * submits the answer to an open question
 * @param {string} token JWT Bearer Token
 * @param {string} zipcode address zipcode
 * @returns Promise of the request to be manipulated by the user
 */
export const getAddressInfo = (token, zipcode) => {
  return get({
    url: `/services/consult-zipcode/${zipcode}`,
    token: token,
  })
}

/**
 * submits the answer to an open question
 * @param {string} token JWT Bearer Token
 * @param {object} data object with response
 * @returns Promise of the request to be manipulated by the user
 */
export const submitReviewAnswer = (token, data) => {
  return put({
    url: '/reviews/',
    token: token,
    data: data,
  })
}

/**
 * Get the antifraude document image
 * @param {string} token JWT Bearer Token
 * @param {string} fileKey The document identifier
 * @returns Promise of the request to be manipulated by the user
 */
export const getAntifraudeImage = (token, fileKey) => {
  return get({
    url: `/antifraude/download_document/${fileKey}`,
    token: token,
    responseType: RESPONSE_TYPE.BLOB,
  })
}


/**
 * get all background check list, can be parametrized
 * @param {string} token JWT Bearer Token
 * @param {object} options object that receives params object to the request
 * @returns Promise of the request to be manipulated by the user
 */
export const getBackgroundChecks = (token, { params }) => {
  return get({
    url: `/background_check/list/paginated`,
    token: token,
    params: params,
  })
}

/**
 * get a background check based on its uuid
 * @param {string} token JWT Bearer Token
 * @param {string} uuid the background check uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const getBackgroundCheck = (token, uuid) => {
  return get({
    url: `/background_check/${uuid}`,
    token: token,
  })
}

/**
 * create a new background check based on the person's cpf
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the person's cpf
 * @returns Promise of the request to be manipulated by the user
 */
export const createBackgroundCheck = (token, data) => {
  return post({
    url: '/background_check/create',
    token: token,
    data: data,
  })
}

/**
 * Get analytics data
 * @param {string} token JWT Bearer Token
 * @param {object} params object that receives params object to the request
 * @returns Promise of the request to be manipulated by the user
 */
export const getReviewAnalytics = (token, { params }) => {
  return get({
    url: '/analytics/all',
    token: token,
    params: params,
  })
}

/**
 * Get the internal users
 * @param {string} token JWT Bearer Token
 * @param {object} options object that receives params object to the request
 * @returns Promise of the request to be manipulated by the user
 */
export const getInternalUsers = (token, { params }) => {
  return get({
    url: '/user/internal/list',
    token: token,
    params: params,
  })
}

/**
 * create a new internal user
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the user data
 * @returns Promise of the request to be manipulated by the user
 */
export const createInternalUser = (token, data) => {
  return post({
    url: '/user/internal',
    token: token,
    data: data,
  })
}

/**
 * update an internal user
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the user data
 * @returns Promise of the request to be manipulated by the user
 */
export const updateInternalUser = (token, data) => {
  return put({
    url: '/user/internal',
    token: token,
    data: data,
  })
}

/**
 * list the available scopes for a given category
 * @param {string} token JWT Bearer Token
 * @param {string} userRole the user role to get the scopes from
 * @returns Promise of the request to be manipulated by the user
 */
export const listScopes = (token, userRole) => {
  return get({
    url: `/scopes/role/${userRole}`,
    token: token,
  })
}

/**
 * create a new company keyword
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the company keyword data
 * @returns Promise of the request to be manipulated by the user
 */
export const createCompanyKeyword = (token, data) => {
  return post({
    url: `/company_keywords/add`,
    token: token,
    data: data,
  })
}

/**
 * update an company keyword
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the company keyword data
 * @returns Promise of the request to be manipulated by the user
 */
export const updateCompanyKeyword = (token, data) => {
  return put({
    url: `/company_keywords/update`,
    token: token,
    data: data,
  })
}

/**
 * delete a company keyword
 * @param {string} token JWT Bearer Token
 * @param {string} keyword_uuid the company keyword uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const deleteCompanyKeyword = (token, keyword_uuid) => {
  return remove({
    url: `/company_keywords/delete`,
    token: token,
    params: { keyword_uuid: keyword_uuid },
  })
}

/**
 * Load company keywords
 * @param {string} token JWT Bearer Token
 * @returns Promise of the request to be manipulated by the user
 */
export const loadCompanyKeywords = (token) => {
  return get({
    url: `/company_keywords/list`,
    token: token,
  })
}

/**
 * create a new process keyword
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the process keyword data
 * @returns Promise of the request to be manipulated by the user
 */
export const createProcessKeyword = (token, data) => {
  return post({
    url: `/process_keywords/add`,
    token: token,
    data: data,
  })
}

/**
 * update a process keyword
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the process keyword data
 * @returns Promise of the request to be manipulated by the user
 */
export const updateProcessKeyword = (token, data) => {
  return put({
    url: `/process_keywords/update`,
    token: token,
    data: data,
  })
}

/**
 * delete a process keyword
 * @param {string} token JWT Bearer Token
 * @param {string} record_uuid the process keyword uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const deleteProcessKeyword = (token, record_uuid) => {
  return remove({
    url: `/process_keywords/delete`,
    token: token,
    params: { record_uuid: record_uuid },
  })
}

/**
 * Load process keywords
 * @param {string} token JWT Bearer Token
 * @returns Promise of the request to be manipulated by the user
 * @returns Promise of the request to be manipulated by the user
 */
export const loadProcessKeywords = (token) => {
  return get({
    url: `/process_keywords/list`,
    token: token,
  })
}

/**
 * Update compliance list record
 * @param {string} token - user authentication token
 * @param {object} data - request data
 * @returns Promise of the request to be manipulated by the user
 */
export const updateComplianceListRecord = (token, data) => {
  return put({
    url: `/compliance_list`,
    token: token,
    data: data,
  })
}

/**
 * delete a compliance list record
 * @param {string} token JWT Bearer Token
 * @param {string} record_uuid the compliance list record uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const deleteComplianceListRecord = (token, record_uuid) => {
  return remove({
    url: `/compliance_list`,
    token: token,
    params: { record_uuid: record_uuid },
  })
}

/**
 * Get all compliance lists
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 * @returns Promise of the request to be manipulated by the user
 */
export const getAllComplianceList = (token, params) => {
  return get({
    url: '/compliance_list/list',
    token: token,
    params: params,
  })
}
/**
 * Create compliance list record
 * @param {string} token - user authentication token
 * @param {object} data - request data
 * @returns Promise of the request to be manipulated by the user
 */
export const createComplianceListRecord = (token, data) => {
  return post({
    url: `/compliance_list`,
    token: token,
    data: data,
  })
}

/**
 * Update cnae record
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the cnae data
 * @returns Promise of the request to be manipulated by the user
 */
export const updateCnaeRecord = (token, data) => {
  return put({
    url: `/cnae/update`,
    token: token,
    data: data,
  })
}

/**
 * get all cnae list, can be parametrized
 * @param {string} token JWT Bearer Token
 * @param {object} options object that receives params object to the request
 * @returns Promise of the request to be manipulated by the user
 */
export const getAllCnaes = (token, { params }) => {
  return get({
    url: '/cnae/list',
    token: token,
    params: params,
  })
}

/**
 * create a new cnae record
 * @param {string} token JWT Bearer Token
 * @param {object} data object containing the cnae data
 * @returns Promise of the request to be manipulated by the user
 */
export const createCnaeRecord = (token, data) => {
  return post({
    url: `/cnae/add_record`,
    token: token,
    data: data,
  })
}

/**
 * delete a cnae record
 * @param {string} token JWT Bearer Token
 * @param {string} record_uuid the cnae record uuid
 * @returns Promise of the request to be manipulated by the user
 */
export const deleteCnae = (token, record_uuid) => {
  return remove({
    url: `/cnae/delete`,
    token: token,
    params: { cnae_uuid: record_uuid },
  })
}


/**
 * Get the application approval stats
 *
 * @param {string} token - The user's authentication token.
 * @param {Object} params - The options for the report.
 * @param {string} params.start_date - The start date of the report.
 * @param {string} params.end_date - The end date of the report.
 * @returns {Promise} A promise that resolves to the report data.
 */
export const applicationApprovalStats = (token, params) => {
  return get({
    url: '/analytics/approval_stats',
    token: token,
    params: params,
  })
}

/**
 * Get the application approval risk stats
 *
 * @param {string} token - The user's authentication token.
 * @param {Object} params - The options for the report.
 * @param {string} params.start_date - The start date of the report.
 * @param {string} params.end_date - The end date of the report.
 * @returns {Promise} A promise that resolves to the report data.
 */
export const applicationApprovalByRiskStats = (token, params) => {
  return get({
    url: '/analytics/approved_risk_stats',
    token: token,
    params: params,
  })
}

/**
 * Get the assessors response time stats
 *
 * @param {string} token - The user's authentication token.
 * @param {Object} params - The options for the report.
 * @param {string} params.start_date - The start date of the report.
 * @param {string} params.end_date - The end date of the report.
 * @returns {Promise} A promise that resolves to the report data.
 */
export const assessorsResponseTimeSLA = (token, params) => {
  return get({
    url: '/analytics/assessors_response_time_stats',
    token: token,
    params: params,
  })
}


/**
 * Get the compliance review stats
 *
 * @param {string} token - The user's authentication token.
 * @param {Object} params - The options for the report.
 * @param {string} params.start_date - The start date of the report.
 * @param {string} params.end_date - The end date of the report.
 * @returns {Promise} A promise that resolves to the report data.
 */
export const complianceAnalysisTimeSLA = (token, params) => {
  return get({
    url: '/analytics/compliance_analysis_time_stats',
    token: token,
    params: params,
  })
}


/**
 * Request a recap of the stuck BGCs
 *
 * @param {string} token - The user's authentication token.
 * @param {string} params.person_name - The name query for the people to be recapped.
 * @param {string} params.person_cpf - The CPF query for the people to be recapped.
 * @param {string} params.start_date - The start date query.
 * @param {string} params.end_date - The end date query.
 * @returns {Promise} A promise that resolves to the recap data.
 */
export const requestRecap = (token, searchParams) => {
  const {
    person_name,
    person_cpf,
    start_date,
    end_date,
    application_linkage_status,
  } = searchParams

  return post({
    url: '/management/recap',
    token: token,
    params: {
      person_name,
      person_cpf,
      start_date,
      end_date,
      application_linkage_status,
    },
  })
}
